import { useState, useMemo } from 'react';
import { usePlanVsIst } from './cpc-hooks';
import type { PlanVsIstItem } from './cpc-types';

const DEVIATION_WARN = 10;
const DEVIATION_CRIT = 25;

function deviationColor(pct: number | null): string {
  if (pct === null) return 'var(--color-text-muted)';
  if (pct > DEVIATION_CRIT) return 'var(--color-danger, #dc3545)';
  if (pct > DEVIATION_WARN) return '#f59e0b';
  if (pct < -DEVIATION_WARN) return 'var(--color-success, #198754)';
  return 'var(--color-text)';
}

function fmt(n: number | null, digits = 1): string {
  if (n === null) return '—';
  return n.toFixed(digits);
}

function fmtPct(n: number | null): string {
  if (n === null) return '—';
  return (n >= 0 ? '+' : '') + n.toFixed(1) + ' %';
}

function SummaryCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div style={{
      background: 'var(--color-bg-card)',
      border: '1px solid var(--color-border)',
      borderRadius: '8px',
      padding: '1rem 1.25rem',
      minWidth: 140,
      flex: '1 1 140px',
    }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: accent ?? 'var(--color-text)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>{sub}</div>}
    </div>
  );
}

export function CpcPlanVsIstPage() {
  const { data = [], isLoading, error } = usePlanVsIst();

  const [filterOnlyWithActual, setFilterOnlyWithActual] = useState(false);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterVersion, setFilterVersion] = useState('');
  const [filterSizeClass, setFilterSizeClass] = useState('');
  const [sortKey, setSortKey] = useState<'createdAt' | 'planPT' | 'actualPT' | 'deviationPct'>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const versions = useMemo(() => [...new Set(data.map((d) => d.factorSetVersion))].sort(), [data]);
  const sizeClasses = useMemo(() => [...new Set(data.map((d) => d.sizeClass))].sort(), [data]);

  const filtered = useMemo(() => {
    let rows = data;
    if (filterOnlyWithActual) rows = rows.filter((r) => r.actualPT !== null);
    if (filterFrom) rows = rows.filter((r) => r.createdAt >= filterFrom);
    if (filterTo) rows = rows.filter((r) => r.createdAt <= filterTo + 'T23:59:59Z');
    if (filterVersion) rows = rows.filter((r) => r.factorSetVersion === filterVersion);
    if (filterSizeClass) rows = rows.filter((r) => r.sizeClass === filterSizeClass);
    return [...rows].sort((a, b) => {
      let av: number, bv: number;
      if (sortKey === 'createdAt') {
        av = new Date(a.createdAt).getTime();
        bv = new Date(b.createdAt).getTime();
      } else if (sortKey === 'planPT') {
        av = a.planPT.expected;
        bv = b.planPT.expected;
      } else if (sortKey === 'actualPT') {
        av = a.actualPT ?? -Infinity;
        bv = b.actualPT ?? -Infinity;
      } else {
        av = a.deviationPct ?? -Infinity;
        bv = b.deviationPct ?? -Infinity;
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [data, filterOnlyWithActual, filterFrom, filterTo, filterVersion, filterSizeClass, sortKey, sortDir]);

  const stats = useMemo(() => {
    const withActual = filtered.filter((r) => r.actualPT !== null);
    const avgDev = withActual.length > 0
      ? withActual.reduce((s, r) => s + (r.deviationPct ?? 0), 0) / withActual.length
      : null;
    const outliers = withActual.filter((r) => Math.abs(r.deviationPct ?? 0) > DEVIATION_CRIT).length;
    return { total: filtered.length, withActual: withActual.length, avgDev, outliers };
  }, [filtered]);

  function toggleSort(key: typeof sortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  }

  function SortIcon({ k }: { k: typeof sortKey }) {
    if (sortKey !== k) return <span style={{ opacity: 0.3 }}> ↕</span>;
    return <span>{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>;
  }

  const thStyle: React.CSSProperties = {
    padding: '0.5rem 0.75rem',
    textAlign: 'left',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: 'var(--color-text-muted)',
    borderBottom: '2px solid var(--color-border)',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  };
  const tdStyle: React.CSSProperties = {
    padding: '0.5rem 0.75rem',
    fontSize: '0.82rem',
    borderBottom: '1px solid var(--color-border)',
    verticalAlign: 'middle',
  };

  if (isLoading) return <p style={{ padding: '2rem', color: 'var(--color-text-muted)' }}>Lädt Reportingdaten…</p>;
  if (error) return <p style={{ padding: '2rem', color: 'var(--color-danger, #dc3545)' }}>Fehler beim Laden der Reportingdaten.</p>;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 1rem' }}>
      <div style={{
        background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
        padding: '1.25rem 1.75rem',
        borderRadius: '8px',
        marginBottom: '1.25rem',
      }}>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'white', margin: 0 }}>Plan vs. Ist</h1>
        <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.75)', margin: '0.25rem 0 0' }}>
          Abweichungsanalyse aller Kalkulationen
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <SummaryCard label="Kalkulationen" value={String(stats.total)} />
        <SummaryCard
          label="Mit Ist-PT"
          value={String(stats.withActual)}
          sub={stats.total > 0 ? `${Math.round((stats.withActual / stats.total) * 100)} %` : undefined}
        />
        <SummaryCard
          label="Ø Abweichung"
          value={fmtPct(stats.avgDev)}
          accent={stats.avgDev !== null ? deviationColor(stats.avgDev) : undefined}
        />
        <SummaryCard
          label={`Ausreißer (>${DEVIATION_CRIT} %)`}
          value={String(stats.outliers)}
          accent={stats.outliers > 0 ? 'var(--color-danger, #dc3545)' : undefined}
        />
      </div>

      {/* Filter Bar */}
      <div style={{
        background: 'var(--color-bg-card)',
        border: '1px solid var(--color-border)',
        borderRadius: '8px',
        padding: '0.875rem 1rem',
        marginBottom: '1rem',
        display: 'flex',
        gap: '0.75rem',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={filterOnlyWithActual}
            onChange={(e) => setFilterOnlyWithActual(e.target.checked)}
          />
          Nur mit Ist-PT
        </label>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem' }}>
          <span style={{ color: 'var(--color-text-muted)' }}>Von</span>
          <input
            type="date"
            value={filterFrom}
            onChange={(e) => setFilterFrom(e.target.value)}
            style={{ fontSize: '0.82rem', padding: '0.25rem 0.5rem', border: '1px solid var(--color-border)', borderRadius: 4, background: 'var(--color-bg)', color: 'var(--color-text)' }}
          />
          <span style={{ color: 'var(--color-text-muted)' }}>bis</span>
          <input
            type="date"
            value={filterTo}
            onChange={(e) => setFilterTo(e.target.value)}
            style={{ fontSize: '0.82rem', padding: '0.25rem 0.5rem', border: '1px solid var(--color-border)', borderRadius: 4, background: 'var(--color-bg)', color: 'var(--color-text)' }}
          />
        </div>

        {versions.length > 1 && (
          <select
            value={filterVersion}
            onChange={(e) => setFilterVersion(e.target.value)}
            style={{ fontSize: '0.82rem', padding: '0.25rem 0.5rem', border: '1px solid var(--color-border)', borderRadius: 4, background: 'var(--color-bg)', color: 'var(--color-text)' }}
          >
            <option value="">Alle Faktor-Versionen</option>
            {versions.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        )}

        {sizeClasses.length > 1 && (
          <select
            value={filterSizeClass}
            onChange={(e) => setFilterSizeClass(e.target.value)}
            style={{ fontSize: '0.82rem', padding: '0.25rem 0.5rem', border: '1px solid var(--color-border)', borderRadius: 4, background: 'var(--color-bg)', color: 'var(--color-text)' }}
          >
            <option value="">Alle Größenklassen</option>
            {sizeClasses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}

        {(filterOnlyWithActual || filterFrom || filterTo || filterVersion || filterSizeClass) && (
          <button
            onClick={() => { setFilterOnlyWithActual(false); setFilterFrom(''); setFilterTo(''); setFilterVersion(''); setFilterSizeClass(''); }}
            style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem', border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer', background: 'transparent', color: 'var(--color-text-muted)' }}
          >
            Filter zurücksetzen
          </button>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <p style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: '2rem 0' }}>
          Keine Kalkulationen gefunden.
        </p>
      ) : (
        <div style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: '8px', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Unternehmen</th>
                <th style={thStyle}>Revision</th>
                <th style={{ ...thStyle, cursor: 'pointer' }} onClick={() => toggleSort('createdAt')}>
                  Datum<SortIcon k="createdAt" />
                </th>
                <th style={thStyle}>Größe</th>
                <th style={thStyle}>Frameworks</th>
                <th style={thStyle}>Schätzunsicherheit</th>
                <th style={thStyle}>Faktor-V.</th>
                <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => toggleSort('planPT')}>
                  Plan-PT (erw.)<SortIcon k="planPT" />
                </th>
                <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => toggleSort('actualPT')}>
                  Ist-PT<SortIcon k="actualPT" />
                </th>
                <th style={{ ...thStyle, textAlign: 'right' }} onClick={() => toggleSort('deviationPct')}>
                  Abweichung<SortIcon k="deviationPct" />
                </th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Quelle</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <PlanVsIstRow key={row.id} row={row} tdStyle={tdStyle} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PlanVsIstRow({ row, tdStyle }: { row: PlanVsIstItem; tdStyle: React.CSSProperties }) {
  const devColor = deviationColor(row.deviationPct);
  const hasDev = row.deviationPct !== null;

  return (
    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
      <td style={tdStyle}>
        <code style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
          {row.companyId.slice(0, 8)}…
        </code>
      </td>
      <td style={tdStyle}>
        <span style={{ fontSize: '0.82rem' }}>{row.revisionLabel || '—'}</span>
      </td>
      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
        {new Date(row.createdAt).toLocaleDateString('de-DE')}
      </td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        <span style={{
          display: 'inline-block',
          padding: '0.1rem 0.45rem',
          borderRadius: 4,
          fontSize: '0.75rem',
          fontWeight: 600,
          background: row.sizeClass === 'L' ? '#dbeafe' : row.sizeClass === 'S' ? '#dcfce7' : '#fef9c3',
          color: row.sizeClass === 'L' ? '#1e40af' : row.sizeClass === 'S' ? '#15803d' : '#854d0e',
        }}>
          {row.sizeClass}
        </span>
      </td>
      <td style={{ ...tdStyle, maxWidth: 180 }}>
        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
          {row.frameworks.map((f) => (
            <span key={f} style={{ fontSize: '0.68rem', padding: '0.1rem 0.35rem', background: 'var(--color-bg-alt, #f3f4f6)', borderRadius: 3, fontFamily: 'monospace' }}>
              {f}
            </span>
          ))}
        </div>
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        <span style={{ fontSize: '0.82rem', color: row.estimationRate > 0.5 ? '#f59e0b' : 'var(--color-text)' }}>
          {(row.estimationRate * 100).toFixed(0)} %
        </span>
      </td>
      <td style={{ ...tdStyle, textAlign: 'center', fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
        {row.factorSetVersion}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        <span>{fmt(row.planPT.expected)} PT</span>
        <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
          [{fmt(row.planPT.min)}–{fmt(row.planPT.max)}]
        </span>
      </td>
      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {row.actualPT !== null ? (
          <span style={{ fontWeight: 600 }}>{fmt(row.actualPT)} PT</span>
        ) : (
          <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>—</span>
        )}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {hasDev ? (
          <div>
            <span style={{ color: devColor, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {fmtPct(row.deviationPct)}
            </span>
            <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
              {row.deviation! >= 0 ? '+' : ''}{fmt(row.deviation)} PT
            </span>
          </div>
        ) : (
          <span style={{ color: 'var(--color-text-muted)' }}>—</span>
        )}
      </td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        {row.actualPTSource === 'clockodo' && (
          <span style={{ fontSize: '0.72rem', padding: '0.15rem 0.45rem', background: '#dbeafe', color: '#1e40af', borderRadius: 4 }}>
            Clockodo
          </span>
        )}
        {row.actualPTSource === 'manual' && (
          <span style={{ fontSize: '0.72rem', padding: '0.15rem 0.45rem', background: '#f3f4f6', color: 'var(--color-text-muted)', borderRadius: 4 }}>
            manuell
          </span>
        )}
        {!row.actualPTSource && (
          <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>—</span>
        )}
      </td>
    </tr>
  );
}
